import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AuthError, requestOtp } from "@/lib/auth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Logo } from "@/components/ui/Logo";
import { ErrorBlock } from "@/components/ui/States";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { TelegramLoginButton } from "@/components/auth/TelegramLoginButton";

/**
 * Passwordless sign-in.
 *
 * One unified screen for new and returning users. The user types an
 * email, we send a 6-digit code via supabase.auth.signInWithOtp
 * (`shouldCreateUser: true` — Supabase auto-creates the account when
 * the email isn't known yet). The next step is /sign-in/verify where
 * the code is exchanged for a session.
 *
 * Existing password-based accounts still work because Supabase issues
 * an OTP for any email it has, regardless of whether a password is set.
 * Nobody loses access.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;
    setError(null);
    setSubmitting(true);
    try {
      await requestOtp(cleanEmail);
      const qs = new URLSearchParams({ email: cleanEmail });
      if (next) qs.set("next", next);
      navigate(`/sign-in/verify?${qs.toString()}`);
    } catch (err) {
      setError(
        err instanceof AuthError ? err.message : "Couldn't send the code. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="w-full max-w-[440px] animate-fade-in">
        <Logo size="md" showTagline className="mb-8" />

        <Card padding="lg">
          <h2 className="text-[28px] font-bold tracking-tight text-[#1a1a1a]">Sign in</h2>
          <p className="mt-2 text-[15px] text-[#6c757d]">
            Sign in with Telegram or get a 6-digit code by email.
          </p>

          {/* Telegram path — single tap, no email needed. New users get an
              auth.users row auto-created server-side; returning Telegram
              users resolve to the same id. Bot notifications get wired up
              from the same authentication exchange. */}
          <div className="mt-6 flex justify-center">
            <TelegramLoginButton />
          </div>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-[#e2e8f0]" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#94a3b8]">
              or
            </span>
            <span className="h-px flex-1 bg-[#e2e8f0]" />
          </div>

          <form className="flex flex-col gap-5" onSubmit={onSubmit}>
            {error ? <ErrorBlock title="Couldn't send the code" message={error} /> : null}

            <Field label="Email" required>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
                autoComplete="email"
                autoFocus
              />
            </Field>

            <Button type="submit" loading={submitting} fullWidth disabled={!email.trim()}>
              {submitting ? "Sending code..." : "Send code"}
            </Button>
          </form>

          <p className="mt-4 text-center text-[13px] text-[#6c757d]">
            New here? Either path creates your account automatically.
          </p>
        </Card>
      </div>
    </PublicLayout>
  );
}
